import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAccountSocialMediaSignatureTemplateMutation } from '../../../../queries/Account/useAccountSocialMediaSignatureTemplateMutation';
import { useTemplate } from '../../../../queries/Templates/useTemplate';
import { Button } from '../../../components-v2/Button';
import { TextArea } from '../../../components-v2/TextArea';
import { error as errorColor } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { LoadingSpinner } from '../../LoadingSpinner';
import { SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS } from '../models';
import { styleSheet as settingsStyleSheet } from '../styles';

const TEXTAREA_ID = 'editSocialMediaDisclaimerDefault';
const ERROR_ID = 'editSocialMediaDisclaimerDefault-error';
const TEMPLATE_NAME = 'Account Social Media Signature';

const styleSheet = StyleSheet.create({
	container: {
		maxWidth: 600,
	},
	errorText: {
		color: errorColor,
		fontSize: '0.75rem',
		marginTop: 4,
	},
	textArea: {
		width: '100%',
		minHeight: 100,
	},
	buttonRow: {
		marginTop: 12,
	},
});

function escapeHtml(input: string): string {
	return input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function extractContent(document: string | undefined): string {
	if (!document) {
		return '';
	}
	try {
		const parser = new DOMParser();
		const parsed = parser.parseFromString(document, 'text/html');
		const levContent = parsed.querySelector('lev-content');
		// textContent auto-decodes HTML entities written via escapeHtml on save.
		// If no wrapper is found, fall back to an empty string rather than dumping raw HTML into a plain textarea.
		return levContent?.textContent ?? '';
	} catch {
		return '';
	}
}

export const SocialMediaDisclaimerSection: React.FC = () => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const accountTemplateId = userSession.account?.preferences?.socialMediaSignatureTemplateId;

	const {
		data: existingTemplate,
		isPending: isLoadingTemplate,
		isError: templateLoadError,
	} = useTemplate<Api.ITemplate>({
		templateId: accountTemplateId,
		enabled: !!accountTemplateId,
		refetchOnMount: 'always',
		impersonationContext: userSession.impersonationContext,
	});

	const initialValue = React.useMemo(
		() => extractContent(existingTemplate?.content?.document),
		[existingTemplate?.content?.document]
	);
	const [value, setValue] = React.useState<string>(initialValue);
	const [isSaving, setIsSaving] = React.useState<boolean>(false);
	// Guards the [initialValue] sync effect so background refetches don't clobber in-progress edits.
	const hasUserEditedRef = React.useRef(false);

	React.useEffect(() => {
		if (hasUserEditedRef.current) {
			return;
		}
		setValue(initialValue);
	}, [initialValue]);

	const templatesVm = React.useMemo(
		() => new Api.TemplatesViewModel(userSession).impersonate(userSession.impersonationContext),
		[userSession]
	);

	const mutation = useAccountSocialMediaSignatureTemplateMutation({
		onSuccess: () => {
			toast.success('Social media disclaimer saved');
			hasUserEditedRef.current = false;
			userSession.account?.load(true);
		},
		onError: err => {
			errorMessages.pushApiError(err);
		},
	});

	const isOverLimit = value.length > SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS;
	const isLoading = !!accountTemplateId && isLoadingTemplate;

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		hasUserEditedRef.current = true;
		setValue(e.target.value);
	};

	const handleSave = async () => {
		if (isOverLimit || isSaving || mutation.isPending) {
			return;
		}

		const trimmed = value.trim();

		// Clearing: drop the reference. Leave the underlying template in place — orphan templates are harmless and
		// avoid us needing template-delete permissions here.
		if (!trimmed) {
			setIsSaving(true);
			try {
				await mutation.mutateAsync({
					templateId: null,
					impersonationContext: userSession.impersonationContext,
				});
			} finally {
				setIsSaving(false);
			}
			return;
		}

		setIsSaving(true);
		try {
			const wrappedContent = `<lev-content>${escapeHtml(trimmed)}</lev-content>`;

			if (accountTemplateId && existingTemplate) {
				const updated: Api.ITemplate = {
					...existingTemplate,
					id: accountTemplateId,
					content: { document: wrappedContent },
				};
				await templatesVm.update(updated);
				await mutation.mutateAsync({
					templateId: accountTemplateId,
					impersonationContext: userSession.impersonationContext,
				});
			} else {
				const template: Api.ITemplate = {
					name: TEMPLATE_NAME,
					templateType: Api.TemplateType.SocialMediaSignature,
					content: { document: wrappedContent },
					scope: Api.TemplateScope.Account,
					defaultTemplate: true,
				};
				const created = await templatesVm.create(template);
				if (!created?.id) {
					throw new Error('Template create did not return an id');
				}
				await mutation.mutateAsync({
					templateId: created.id,
					impersonationContext: userSession.impersonationContext,
				});
			}
		} catch (error) {
			errorMessages.pushApiError(error as Api.IOperationResultNoValue);
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return (
			<div className={css(styleSheet.container)}>
				<LoadingSpinner type='small' />
				<span>Loading disclaimer...</span>
			</div>
		);
	}

	// Refuse to render the editor if the existing template id failed to load — saving in this state would silently
	// orphan the previous template by falling into the create branch.
	if (templateLoadError && !!accountTemplateId) {
		return (
			<div className={css(styleSheet.container)}>
				<p className={css(styleSheet.errorText)}>
					Could not load the current disclaimer. Refresh the page to try again.
				</p>
			</div>
		);
	}

	const isPending = isSaving || mutation.isPending;

	return (
		<div className={css(styleSheet.container)}>
			<label className={css(settingsStyleSheet.formFieldLabel)} htmlFor={TEXTAREA_ID}>
				Default disclaimer
			</label>
			<TextArea
				id={TEXTAREA_ID}
				aria-describedby={isOverLimit ? ERROR_ID : undefined}
				aria-invalid={isOverLimit || undefined}
				placeholder='Enter a URL or disclaimer text...'
				value={value}
				onChange={handleChange}
				styles={[styleSheet.textArea]}
			/>
			{isOverLimit && (
				<p id={ERROR_ID} className={css(styleSheet.errorText)}>
					Disclaimer must be {SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS} characters or fewer ({value.length}/
					{SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS}).
				</p>
			)}
			<div className={css(baseStyleSheet.mb2, styleSheet.buttonRow)}>
				<Button variant='primary' size='sm' onClick={handleSave} disabled={isPending || isOverLimit}>
					{isPending ? 'Saving...' : 'Save'}
				</Button>
			</div>
		</div>
	);
};
